import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { todayIsoBrt } from '@/lib/dateBrt';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return String(iso);
  }
};

const ACTION_LABEL = {
  create: 'Criação',
  update: 'Alteração',
  delete: 'Exclusão',
  cancel: 'Cancelamento',
  duplicate: 'Duplicação',
  apply_block: 'Aplicação de Bloco',
};

const ENTITY_LABEL = {
  project: 'Projeto',
  phase: 'Fase',
  task: 'Tarefa',
  phase_block: 'Bloco',
};

const downloadBlob = (resp, filename) => {
  const url = window.URL.createObjectURL(new Blob([resp.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ============================================================
// ACCESS LOGS MODAL
// ============================================================
export function AccessLogsModal({ open, onOpenChange, users }) {
  const [filters, setFilters] = useState({ user_id: 'all', success: 'all', start_date: '', end_date: '' });
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.success !== 'all') params.success = filters.success === 'true';
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const { data } = await api.get('/access-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar logs de acesso');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (open) fetchLogs();
  }, [open, fetchLogs]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.success !== 'all') params.success = filters.success === 'true';
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const resp = await api.get('/access-logs/export/excel', { params, responseType: 'blob' });
      downloadBlob(resp, `logs_acesso_${todayIsoBrt()}.xlsx`);
      toast.success('Relatório exportado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao exportar');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Relatório de Logs de Acesso</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 py-2">
          <div>
            <Label className="text-xs">Usuário</Label>
            <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
              <SelectTrigger data-testid="access-log-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Resultado</Label>
            <Select value={filters.success} onValueChange={(v) => setFilters({ ...filters, success: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="true">Sucesso</SelectItem>
                <SelectItem value="false">Falha</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 py-2 border-b">
          <span className="text-sm text-gray-600">Mostrando {logs.length} de {total} registros</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={fetchLogs} disabled={loading} data-testid="access-log-refresh">
              {loading ? 'Carregando...' : 'Atualizar'}
            </Button>
            <Button size="sm" onClick={handleExport} disabled={exporting} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="access-log-export">
              {exporting ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="text-left py-2 px-3">Data/Hora</th>
                <th className="text-left py-2 px-3">Usuário</th>
                <th className="text-left py-2 px-3">Papel</th>
                <th className="text-left py-2 px-3">Resultado</th>
                <th className="text-left py-2 px-3">Motivo</th>
                <th className="text-left py-2 px-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-6 text-gray-500">Nenhum registro encontrado</td></tr>
              ) : logs.map((l) => (
                <tr key={l.log_id} className="border-b">
                  <td className="py-2 px-3 whitespace-nowrap">{formatDateTime(l.login_at)}</td>
                  <td className="py-2 px-3">{l.full_name || l.email}</td>
                  <td className="py-2 px-3 text-xs text-gray-600">{l.role || '-'}</td>
                  <td className="py-2 px-3">
                    {l.success
                      ? <Badge className="bg-green-100 text-green-700 border-green-200">Sucesso</Badge>
                      : <Badge className="bg-red-100 text-red-700 border-red-200">Falha</Badge>}
                  </td>
                  <td className="py-2 px-3 text-xs text-gray-600">{l.failure_reason || '-'}</td>
                  <td className="py-2 px-3 text-xs text-gray-500">{l.ip || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// AUDIT LOGS MODAL
// ============================================================
export function AuditLogsModal({ open, onOpenChange, users }) {
  const [filters, setFilters] = useState({ user_id: 'all', entity_type: 'all', action: 'all', start_date: '', end_date: '' });
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const { data } = await api.get('/audit-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar logs de edição');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (open) fetchLogs();
  }, [open, fetchLogs]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const resp = await api.get('/audit-logs/export/excel', { params, responseType: 'blob' });
      downloadBlob(resp, `logs_edicao_${todayIsoBrt()}.xlsx`);
      toast.success('Relatório exportado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao exportar');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Relatório de Logs de Edição</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 py-2">
          <div>
            <Label className="text-xs">Usuário</Label>
            <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
              <SelectTrigger data-testid="audit-log-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={filters.entity_type} onValueChange={(v) => setFilters({ ...filters, entity_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="project">Projeto</SelectItem>
                <SelectItem value="phase">Fase</SelectItem>
                <SelectItem value="task">Tarefa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ação</Label>
            <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="create">Criação</SelectItem>
                <SelectItem value="update">Alteração</SelectItem>
                <SelectItem value="delete">Exclusão</SelectItem>
                <SelectItem value="cancel">Cancelamento</SelectItem>
                <SelectItem value="duplicate">Duplicação</SelectItem>
                <SelectItem value="apply_block">Aplicação de Bloco</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 py-2 border-b">
          <span className="text-sm text-gray-600">Mostrando {logs.length} de {total} registros</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={fetchLogs} disabled={loading} data-testid="audit-log-refresh">
              {loading ? 'Carregando...' : 'Atualizar'}
            </Button>
            <Button size="sm" onClick={handleExport} disabled={exporting} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="audit-log-export">
              {exporting ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="text-left py-2 px-3">Data/Hora</th>
                <th className="text-left py-2 px-3">Usuário</th>
                <th className="text-left py-2 px-3">Ação</th>
                <th className="text-left py-2 px-3">Tipo</th>
                <th className="text-left py-2 px-3">Item</th>
                <th className="text-left py-2 px-3">Projeto</th>
                <th className="text-left py-2 px-3">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-6 text-gray-500">Nenhum registro encontrado</td></tr>
              ) : logs.map((l) => (
                <tr key={l.audit_id} className="border-b">
                  <td className="py-2 px-3 whitespace-nowrap">{formatDateTime(l.created_at)}</td>
                  <td className="py-2 px-3">{l.user_name || '-'}</td>
                  <td className="py-2 px-3">
                    <Badge variant="outline" className="text-xs">{ACTION_LABEL[l.action] || l.action}</Badge>
                  </td>
                  <td className="py-2 px-3 text-xs text-gray-600">{ENTITY_LABEL[l.entity_type] || l.entity_type}</td>
                  <td className="py-2 px-3 max-w-xs truncate" title={l.entity_name}>{l.entity_name || '-'}</td>
                  <td className="py-2 px-3 max-w-xs truncate" title={l.project_name}>{l.project_name || '-'}</td>
                  <td className="py-2 px-3 text-xs text-gray-500 max-w-md truncate" title={JSON.stringify(l.details)}>
                    {l.details && Object.keys(l.details).length > 0
                      ? Object.entries(l.details).map(([k, v]) => `${k}: ${v}`).join('; ')
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
